import { motion } from 'framer-motion';
import { fadeUp, stagger } from './motionPresets';

// Decorative wave shape used in purple background of cards
function WaveDecor({ position = 'right' }) {
  const isLeft = position === 'left';
  return (
    <svg
      aria-hidden="true"
      className={`absolute bottom-0 ${isLeft ? 'left-0' : 'right-0'} pointer-events-none`}
      width="100%"
      height="60%"
      viewBox="0 0 400 240"
      preserveAspectRatio="none"
      style={{ transform: isLeft ? 'scaleX(-1)' : 'none' }}
    >
      <path
        d="M 0 240 C 80 200, 140 140, 220 120 S 340 60, 400 80 L 400 240 Z"
        fill="rgba(255,255,255,0.08)"
      />
      <path
        d="M 0 240 C 60 210, 160 180, 240 170 S 360 140, 400 150 L 400 240 Z"
        fill="rgba(255,255,255,0.05)"
      />
    </svg>
  );
}

export default function HowItWorksSection() {
  return (
    <section className="py-20 lg:py-28" style={{ backgroundColor: '#EEF3FF' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="text-center mb-16">
          <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-3">How It Works</p>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight mb-4">Three steps to compliance</h2>
          <p className="text-lg text-[#64748B] max-w-2xl mx-auto">Get started in minutes, not weeks.</p>
        </motion.div>

        {/* Bento Grid — Pure CSS */}
        <div className="max-w-[960px] mx-auto grid grid-cols-1 md:grid-cols-[1fr_1.15fr] md:grid-rows-[1fr_1fr] gap-4 md:gap-x-6 md:gap-y-4" style={{ filter: 'drop-shadow(0 8px 24px rgba(79, 70, 229, 0.15))' }}>

          {/* Card 1 — Add Employees (top-left) */}
          <motion.div {...stagger} transition={{ duration: 0.4 }} className="relative rounded-[16px] overflow-hidden bg-gradient-to-br from-[#5B4CFF] to-[#3B7BF7] p-5 md:p-6 pb-0 flex flex-col min-h-[280px] md:min-h-0">
            <WaveDecor position="right" />
            <div className="relative flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0 flex-1">
                <h3 className="text-base md:text-[17px] font-bold text-white mb-1.5">Add Employees</h3>
                <p className="text-xs md:text-[13px] text-white/70 leading-[1.6]">Import or add H-1B, H-1B1, and E-3 employees with visa details, LCA dates, and worksite information.</p>
              </div>
              <span className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-white/15 flex items-center justify-center text-white/80 font-bold text-lg md:text-xl flex-shrink-0">1</span>
            </div>
            <div className="relative mt-auto -ml-5 md:-ml-6 w-[70%]">
              <div
                className="bg-white px-4 py-6 md:py-8 flex justify-center items-center shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
                style={{
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: '40px',
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                }}
              >
                <div className="inline-flex items-center gap-2 bg-[#4F46E5] rounded-full px-5 md:px-6 py-2.5">
                  <span className="text-white font-bold text-base">+</span>
                  <span className="text-white font-semibold text-xs md:text-sm">Add Employee</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Card 3 — Stay Compliant (right, spans 2 rows on md+) */}
          <motion.div {...stagger} transition={{ duration: 0.4, delay: 0.1 }} className="relative rounded-[16px] overflow-hidden bg-gradient-to-br from-[#5B4CFF] to-[#3B7BF7] p-5 md:p-6 pb-0 md:row-span-2 flex flex-col order-3 md:order-none">
            <WaveDecor position="right" />
            <div className="relative flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0 flex-1">
                <h3 className="text-base md:text-[17px] font-bold text-white mb-1.5">Stay Compliant</h3>
                <p className="text-xs md:text-[13px] text-white/70 leading-[1.6]">Track compliance status in real-time with green/yellow/red indicators and automated alerts.</p>
              </div>
              <span className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-white/15 flex items-center justify-center text-white/80 font-bold text-lg md:text-xl flex-shrink-0">3</span>
            </div>
            <div className="relative flex-1 mx-auto w-[95%] bg-white rounded-t-2xl px-4 md:px-6 pt-4 md:pt-5 pb-5 md:pb-6 shadow-xl flex flex-col justify-center">
              <p className="font-bold text-[#0F172A] text-base md:text-lg mb-4 md:mb-5">Compliance Overview</p>
              <div className="flex items-center justify-center gap-6 md:gap-10">
                <div className="relative w-24 h-24 md:w-32 md:h-32 flex-shrink-0">
                  <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#EEF2FF" strokeWidth="7" />
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#4F46E5" strokeWidth="7" strokeDasharray="251 251" strokeLinecap="round" />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-xl md:text-2xl font-bold text-[#0F172A]">100%</span>
                </div>
                <div className="space-y-3 md:space-y-5">
                  <div className="flex items-center gap-2 md:gap-3"><div className="w-3 h-3 md:w-3.5 md:h-3.5 rounded-full bg-emerald-500" /><span className="text-sm md:text-base text-[#0F172A]">Compliant</span></div>
                  <div className="flex items-center gap-2 md:gap-3"><div className="w-3 h-3 md:w-3.5 md:h-3.5 rounded-full bg-amber-400" /><span className="text-sm md:text-base text-[#0F172A]">Warning</span></div>
                  <div className="flex items-center gap-2 md:gap-3"><div className="w-3 h-3 md:w-3.5 md:h-3.5 rounded-full bg-red-500" /><span className="text-sm md:text-base text-[#0F172A] whitespace-nowrap">Non-Compliant</span></div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Card 2 — Upload Documents (bottom-left) — mirrored: white container on bottom-right */}
          <motion.div {...stagger} transition={{ duration: 0.4, delay: 0.2 }} className="relative rounded-[16px] overflow-hidden bg-gradient-to-br from-[#4C6FFF] to-[#3B5BDB] p-5 md:p-6 pb-0 flex flex-col min-h-[280px] md:min-h-0 order-2 md:order-none">
            <WaveDecor position="left" />
            <div className="relative flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0 flex-1">
                <h3 className="text-base md:text-[17px] font-bold text-white mb-1.5">Upload Documents</h3>
                <p className="text-xs md:text-[13px] text-white/70 leading-[1.6]">Drag and drop required PAF documents — LCA, wage records, notifications, and more.</p>
              </div>
              <span className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-white/15 flex items-center justify-center text-white/80 font-bold text-lg md:text-xl flex-shrink-0">2</span>
            </div>
            <div className="relative mt-auto ml-auto -mr-5 md:-mr-6 w-[70%]">
              <div
                className="bg-white px-4 py-6 md:py-8 flex justify-center items-center shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
                style={{
                  borderTopLeftRadius: '40px',
                  borderTopRightRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                }}
              >
                <div className="inline-flex items-center gap-2 bg-[#4F46E5] rounded-full px-5 md:px-6 py-2.5">
                  <span className="text-white font-semibold text-xs md:text-sm">Create your first PAF</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
